import { z } from 'zod';

export const AuditEventTypeSchema = z.enum(['PURCHASE', 'MANUAL_GRANT', 'MANUAL_REVOKE']);
export type AuditEventType = z.infer<typeof AuditEventTypeSchema>;

/** A local input must survive a round trip, including DST and invalid dates. */
export function auditLocalTimeToUtc(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/.test(value)) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  const pad = (number: number) => String(number).padStart(2, '0');
  const local = `${String(date.getFullYear()).padStart(4, '0')}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  if (local !== (value.length === 16 ? `${value}:00` : value)) return null;
  return date.toISOString();
}

export const SubscriptionAuditFilterSchema = z.object({
  searchBy: z.enum(['id', 'username']),
  query: z.string().max(100, '會員帳號最多 100 字元').refine(value => !/[\p{Cc}]/u.test(value), '不可包含控制字元').transform(value => value.trim()),
  fromLocal: z.string(),
  toLocal: z.string(),
  type: z.union([z.literal('ALL'), AuditEventTypeSchema]),
}).superRefine((value, ctx) => {
  if (value.query && value.searchBy === 'id' && (!/^[1-9]\d*$/.test(value.query) || !Number.isSafeInteger(Number(value.query)))) {
    ctx.addIssue({ code: 'custom', path: ['query'], message: '會員帳號 ID 須為正整數' });
  }
  if (!!value.fromLocal !== !!value.toLocal) {
    ctx.addIssue({ code: 'custom', path: [value.fromLocal ? 'toLocal' : 'fromLocal'], message: '請同時填寫開始與結束時間' });
    return;
  }
  if (!value.fromLocal) return;
  const from = auditLocalTimeToUtc(value.fromLocal);
  const to = auditLocalTimeToUtc(value.toLocal);
  if (!from) ctx.addIssue({ code: 'custom', path: ['fromLocal'], message: '請輸入有效的本地時間' });
  if (!to) ctx.addIssue({ code: 'custom', path: ['toLocal'], message: '請輸入有效的本地時間' });
  if (from && to && from >= to) ctx.addIssue({ code: 'custom', path: ['toLocal'], message: '結束時間須晚於開始時間' });
});
export type SubscriptionAuditFilterDTO = z.infer<typeof SubscriptionAuditFilterSchema>;

export const auditFilterDefaults: SubscriptionAuditFilterDTO = { searchBy: 'username', query: '', fromLocal: '', toLocal: '', type: 'ALL' };
export type AuditQueryFilters = { searchBy?: 'id' | 'username'; query?: string; from?: string; to?: string; type?: AuditEventType };
export function auditQueryFilters(input: SubscriptionAuditFilterDTO): AuditQueryFilters {
  const value = SubscriptionAuditFilterSchema.parse(input);
  return {
    ...(value.query ? { searchBy: value.searchBy, query: value.query } : {}),
    ...(value.fromLocal ? { from: auditLocalTimeToUtc(value.fromLocal)!, to: auditLocalTimeToUtc(value.toLocal)! } : {}),
    ...(value.type === 'ALL' ? {} : { type: value.type }),
  };
}
